import React, { useEffect, useState, useCallback } from 'react';
import { FiX, FiBell, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { getSocket } from '../services/socket';
import { AUTH_API_BASE_URL as API_BASE_URL, LEADS_API_BASE_URL } from '../LeadMng/config/api';

const typeTitle = (type) => {
  if (type === 'task') return 'New Task Assigned';
  if (type === 'ticket') return 'Support Ticket Assigned';
  if (type === 'project') return 'Added to Project';
  if (type === 'milestone') return 'Milestone Assigned';
  if (type === 'follow_up_overdue') return '🔴 Follow-up Overdue';
  if (type === 'follow_up_today') return '📞 Follow-up Today';
  if (type === 'follow_up_upcoming') return '📅 Upcoming Follow-up';
  return 'New Lead Assigned';
};

const AssignmentNotificationPopup = () => {
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const handleNotificationClick = useCallback(async (item) => {
    // Dismiss/Mark as read
    await dismissOne(item.id, item.source);

    const isTask = item.type === 'task' || 
                   item.type?.toLowerCase().includes('task') || 
                   item.title?.toLowerCase().includes('task') || 
                   item.message?.toLowerCase().includes('task');

    const isLead = item.source === 'lead' || 
                   item.type === 'lead' || 
                   item.type?.toLowerCase().includes('lead') || 
                   item.title?.toLowerCase().includes('lead') || 
                   item.message?.toLowerCase().includes('lead');

    if (isTask) {
      localStorage.setItem('clickedTaskNotification', JSON.stringify(item));
      localStorage.setItem('activeDashboardTab', 'tasks');
      window.dispatchEvent(new CustomEvent('switchDashboardTab', { detail: { tab: 'tasks' } }));
      navigate('/tasks');
    } else if (isLead) {
      const leadId = item.meta?.leadId || item.leadId;
      if (leadId) {
        navigate(`/lead/${leadId}`);
      } else {
        localStorage.setItem('activeDashboardTab', 'leads');
        window.dispatchEvent(new CustomEvent('switchDashboardTab', { detail: { tab: 'leads' } }));
        navigate('/lead-mng');
      }
    }
  }, [navigate]);


  const fetchUnread = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [res, leadRes] = await Promise.all([
        fetch(`${API_BASE_URL}/notifications`, { headers }).catch(() => null),
        fetch(`${LEADS_API_BASE_URL}/lead-notifications/all`, { headers }).catch(() => null)
      ]);

      let unreadGlobal = [];
      if (res && res.ok) {
        const data = await res.json();
        unreadGlobal = (Array.isArray(data) ? data : [])
          .filter(n => n.unread)
          .map(n => ({
            id: n.id,
            type: n.type,
            source: 'global',
            message: n.message,
            createdAt: n.createdAt,
            meta: n.meta || null
          }));
      }

      let unreadLeads = [];
      if (leadRes && leadRes.ok) {
        const leadData = await leadRes.json();
        const dataArray = Array.isArray(leadData) ? leadData : (Array.isArray(leadData.data) ? leadData.data : (Array.isArray(leadData.notifications) ? leadData.notifications : []));
        unreadLeads = dataArray
          .filter(n => n.isRead === false || n.read === false || !n.isRead)
          .map(n => ({
            id: n.id || n._id,
            type: n.type || 'lead_assigned',
            source: 'lead',
            message: n.message || `New interaction on lead ${n.leadName || ''}`,
            createdAt: n.createdAt || new Date().toISOString(),
            meta: { companyName: n.leadCompany || '' }
          }));
      }

      // Merge and sort by createdAt desc
      const merged = [...unreadGlobal, ...unreadLeads].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // De-duplicate by normalized message to prevent double-rendering if returned by both APIs
      const unique = [];
      const seen = new Set();
      for (const item of merged) {
        const msg = (item.message || '').trim().toLowerCase();
        if (!seen.has(msg)) {
          seen.add(msg);
          unique.push(item);
        }
      }

      setItems(prevItems => {
        const fetchedItems = unique.slice(0, 5);
        if (fetchedItems.length === 0) {
          setVisible(false);
          return fetchedItems;
        }

        const prevIds = new Set(prevItems.map(i => i.id));
        const hasNew = fetchedItems.some(item => !prevIds.has(item.id));
        
        if (prevItems.length === 0) {
          setVisible(true);
        } else if (hasNew) {
          setVisible(true);
        }
        return fetchedItems;
      });
    } catch (e) {
      // silent
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await Promise.all([
        fetch(`${API_BASE_URL}/notifications/mark-all-read`, { method: 'PUT', headers }).catch(() => null),
        fetch(`${LEADS_API_BASE_URL}/lead-notifications/mark-all-read`, { method: 'PUT', headers }).catch(() => null)
      ]);

      setItems([]);
      setVisible(false);
    } catch (e) {
      // silent
    }
  }, []);

  const dismissOne = useCallback(async (id, source) => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) return;
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (source === 'lead') {
        await fetch(`${LEADS_API_BASE_URL}/lead-notifications/${id}/read`, {
          method: 'PATCH',
          headers
        });
      } else {
        await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
          method: 'PATCH',
          headers
        });
      }
    } catch (e) {
      // silent
    } finally {
      setItems(prev => {
        const next = prev.filter(i => i.id !== id);
        if (next.length === 0) setVisible(false);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const socket = getSocket();
    const onNew = (payload) => {
      const isLead = payload.source === 'lead' || 
                     payload.type?.toLowerCase().includes('lead') || 
                     payload.title?.toLowerCase().includes('lead') || 
                     payload.message?.toLowerCase().includes('lead');
      
      const normalizedPayload = {
        ...payload,
        source: isLead ? 'lead' : (payload.source || 'global'),
        unread: true
      };

      setItems(prev => {
        const exists = prev.some(i => i.id === normalizedPayload.id);
        const next = exists ? prev : [normalizedPayload, ...prev].slice(0, 5);
        if (next.length > 0) setVisible(true);
        return next;
      });
      try {
        if (typeof window !== 'undefined' && 'Notification' in window && window.Notification && window.Notification.permission === 'granted') {
          new window.Notification(typeTitle(normalizedPayload.type), {
            body: normalizedPayload.message || '',
            tag: normalizedPayload.id
          });
        }
      } catch (e) {
        console.warn('Failed to display browser notification:', e);
      }
    };
    socket.on('notification:new', onNew);
    try {
      if (typeof window !== 'undefined' && 'Notification' in window && window.Notification && window.Notification.permission === 'default') {
        window.Notification.requestPermission().catch(() => {});
      }
    } catch (e) {
      console.warn('Failed to request notification permission:', e);
    }
    // Fallback polling to ensure fetch even if socket missed
    const interval = setInterval(fetchUnread, 20000);
    // Listen to app-level events to refetch immediately
    const onTaskCreated = () => fetchUnread();
    const onTaskShifted = () => fetchUnread();
    const onLeadAssigned = () => fetchUnread();
    const onTicketAssigned = () => fetchUnread();
    const onProjectAssigned = () => fetchUnread();
    const onMilestoneAssigned = () => fetchUnread();
    window.addEventListener('taskCreated', onTaskCreated);
    window.addEventListener('lead-assigned', onLeadAssigned);
    window.addEventListener('task-shifted', onTaskShifted);
    window.addEventListener('ticket-assigned', onTicketAssigned);
    window.addEventListener('project-assigned', onProjectAssigned);
    window.addEventListener('milestone-assigned', onMilestoneAssigned);
    return () => {
      socket.off('notification:new', onNew);
      clearInterval(interval);
      window.removeEventListener('taskCreated', onTaskCreated);
      window.removeEventListener('lead-assigned', onLeadAssigned);
      window.removeEventListener('task-shifted', onTaskShifted);
      window.removeEventListener('ticket-assigned', onTicketAssigned);
      window.removeEventListener('project-assigned', onProjectAssigned);
      window.removeEventListener('milestone-assigned', onMilestoneAssigned);
    };
  }, [fetchUnread]);

  useEffect(() => {
    if (visible && items.length > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, items]);

  if (!visible || items.length === 0) return null;

  return (
    <>
      <style>{`
        .notification-popup-anim {
          animation: slide-in-mobile 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @media (min-width: 640px) {
          .notification-popup-anim {
            animation: slide-in-desktop 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        }
        @keyframes slide-in-mobile {
          from {
            transform: translate(-50%, -30px);
            opacity: 0;
          }
          to {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
        @keyframes slide-in-desktop {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="fixed top-4 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-auto sm:top-auto sm:bottom-6 sm:right-6 z-[2000] w-[calc(100vw-2rem)] sm:w-[22rem] max-w-sm space-y-3 pointer-events-auto notification-popup-anim">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <FiBell className="text-indigo-600" />
              </div>
              <span className="font-semibold text-gray-800">Notifications</span>
              <span className="ml-1 text-xs px-2 py-0.5 bg-red-500 text-white rounded-full">{items.length}</span>
            </div>
            <button
              onClick={markAllRead}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
              title="Mark all as read"
            >
              <FiCheckCircle /> Clear
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.map(item => (
              <div key={item.id} className="px-4 py-3 border-b border-gray-50 last:border-b-0">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2" />
                  <div 
                    className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => handleNotificationClick(item)}
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {typeTitle(item.type)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {item.message}
                    </div>
                    {item.meta && item.meta.companyName && (
                      <div className="text-xs text-indigo-600 font-medium mt-0.5">
                        {item.meta.companyName}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1">
                      {new Date(item.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => dismissOne(item.id, item.source)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    title="Dismiss"
                  >
                    <FiX />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignmentNotificationPopup;
